import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  CreateCompliance,
  FilterResponse,
  FindManyCompliance,
  FindOne,
  Compliance,
  Option,
  ORGANISATION_CATEGORY,
  PaginateResult,
  UpdateCompliance,
  UpdateComplianceStatus,
} from '@univacity/shared/types';
import { ObjectUtil } from '@univacity/shared/util';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Configuration } from '../config/configuration';

@Injectable({
  providedIn: 'root',
})
export class ComplianceService {
  private baseUrl: string;

  constructor(private http: HttpClient, private config: Configuration) {
    this.baseUrl = this.config.apiMainUrl + '/compliance';
  }

  create(compliance: CreateCompliance): Observable<Compliance> {
    return this.http.post<Compliance>(this.baseUrl, compliance);
  }

  find(query: FindManyCompliance = {}): Observable<PaginateResult<Compliance>> {
    const q = { ...query };
    if (!q.sort || q.sort.includes('recommended')) {
      q.sort = ['isPartner, -1', 'views, -1', 'name,1'];
      if (q.search) q.sort = undefined;
    }
    const params = new HttpParams({ fromObject: q as Params });
    return this.http.get<PaginateResult<Compliance>>(this.baseUrl, {
      params,
    });
  }

  findMinimal(
    query: FindManyCompliance = {}
  ): Observable<PaginateResult<Compliance>> {
    const q = { ...query };

    if (!q.sort || q.sort.includes('recommended')) {
      q.sort = ['isPartner, -1', 'views, -1', 'name,1'];
      if (q.search) q.sort = undefined;
    }
    if (!q.select)
      q.select = [
        'name',
        'logo',
        'cover',
        'type',
        'isPartner',
        'slug',
        'address.country',
        'currency',
      ];
    const params = new HttpParams({ fromObject: q as Params });
    return this.http.get<PaginateResult<Compliance>>(this.baseUrl, {
      params,
    });
  }
  findMinimalHigerEdOnly(
    query: FindManyCompliance = {}
  ): Observable<PaginateResult<Compliance>> {
    return this.findMinimal({
      ...query,
      category: [ORGANISATION_CATEGORY.provider],
    });
  }
  findMinimalAgencyOnly(
    query: FindManyCompliance = {}
  ): Observable<PaginateResult<Compliance>> {
    return this.findMinimal({
      ...query,
      category: [ORGANISATION_CATEGORY.agency],
    });
  }
  findOne(
    compliance: Compliance | string,
    query: FindOne = { lean: true }
  ): Observable<Compliance> {
    const id = typeof compliance === 'string' ? compliance : compliance._id;
    const params = new HttpParams({ fromObject: query as Params });
    return this.http.get<Compliance>(`${this.baseUrl}/${id}`, { params });
  }

  update(id: string, compliance: UpdateCompliance): Observable<Compliance> {
    return this.http.patch<Compliance>(`${this.baseUrl}/${id}`, compliance);
  }

  sumbit(id: string, compliance: UpdateCompliance): Observable<Compliance> {
    return this.http.patch<Compliance>(
      `${this.baseUrl}/${id}/submit`,
      compliance
    );
  }
  activate(
    id: string,
    compliance: UpdateComplianceStatus
  ): Observable<Compliance> {
    return this.http.patch<Compliance>(
      `${this.baseUrl}/${id}/activate`,
      compliance
    );
  }
  requestInfo(
    id: string,
    compliance: UpdateComplianceStatus
  ): Observable<Compliance> {
    return this.http.patch<Compliance>(
      `${this.baseUrl}/${id}/request-info`,
      compliance
    );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  filter(
    filter: string,
    query: FindManyCompliance = {}
  ): Observable<PaginateResult<FilterResponse<string>>> {
    const params = new HttpParams({
      fromObject: ObjectUtil.removeEmptyProperties({
        ...query,
        filter,
      }) as Params,
    });
    return this.http.get<PaginateResult<FilterResponse<string>>>(
      `${this.baseUrl}/filter`,
      { params }
    );
  }

  count(query: FindManyCompliance = {}): Observable<number> {
    const params = new HttpParams({ fromObject: query as Params });
    return this.http.get<number>(this.baseUrl + '/count', { params });
  }

  autoComplete = (
    search: string,
    page?: number,
    query: FindManyCompliance = {}
  ): Observable<{ options: Option[]; nextPage?: number | null }> => {
    const params = ObjectUtil.removeEmptyProperties({
      ...query,
      search,
      page,
      sort: ['isPartner, -1', 'name,1'],
      select: ['name'],
    });

    return this.http
      .get<PaginateResult<Compliance>>(this.baseUrl, {
        params,
      })
      .pipe(
        map((res) => {
          const options = res.docs.map((i) => ({
            label: i.name,
            value: i._id,
          }));
          return { options, nextPage: res.nextPage };
        })
      );
  };
  nameAutoComplete = (
    search: string,
    page?: number,
    query: FindManyCompliance = {}
  ): Observable<{ options: Option[]; nextPage?: number | null }> => {
    return this.http
      .get<PaginateResult<Pick<Compliance, 'name'>>>(this.baseUrl, {
        params: ObjectUtil.removeEmptyProperties({
          ...query,
          search,
          page,
          select: ['name'],
        }),
      })
      .pipe(
        map((res) => {
          const options: Option[] = res.docs.map((i) => ({
            label: i.name,
            value: i.name,
          }));
          return { options, nextPage: res.nextPage };
        })
      );
  };
}
